// 다계약 표시 차수3 정정2(26-08-06) — '판매 전용 계약'(모든 줄이 SALE) 판정은 **shared 정본
// 한 벌**이다. 서버가 네 칸의 기준에서 판매 전용을 빼는 판정(customer-master.row
// pickTermBasisContract)과 재계약율 집계가 부르는 **그 함수**다. 화면이 리터럴 비교('SALE')를
// 새로 적으면 "서버는 빼는데 화면은 반납일을 찍는" 어긋남이 열린다.
import { isSaleOnlyContract } from '@bhops/shared';
import type { CustomerMasterRow } from '@/api/customer-master';
import { kstYmd } from '@/lib/format-date';
import { formatDdayOrNull } from '@/lib/d-day';

// ============================================================
// 다계약 표시 차수3(26-08-05) — '반납일을 지켜볼 계약'을 화면이 읽는 규약 한 벌
// ============================================================
//
// 오너 사양: 계약기간 · 도입일 · 반납일 · D-Day 는 **구독(판매 전용이 아닌) 계약** 기준이고,
// 구독이 여럿이면 먼저 끝나는 계약을 따른다. 판매는 소유가 고객사로 넘어가 **반납이라는
// 사건이 없어** 판매만 있는 고객사는 그 칸들이 '–' 인 것이 맞다.
//
// 판정 자체는 **서버 한 벌**이다(apps/api/src/customer-master/customer-master.row.ts
// pickTermBasisContract). 화면이 하는 일은 둘뿐이고, 그 둘이 이 파일에 있다:
//   ① 서버가 그 판정을 내렸는가를 보고, **안 내렸으면** 종전 화면대로 최신 계약을 보여 준다.
//   ② 기준 계약이 없을 때 **왜 없는지**를 사람이 읽는 문구로 옮긴다.
//
// ⚠ `row.soonestContract ?? row.contract` 로 무조건 폴백하면 **판매 계약의 반납일·D-Day 가
//   다시 찍혀** 오너 사양이 깨진다. 실제로 고객사 상세 헤더가 그 폴백을 남겨 두어, 시트가
//   '–' + '판매 — 반납 없음' 이라고 말하는 고객사의 헤더에 'D-1094'(판매 계약의 잔여일)를
//   찍고 있었다 — 두 화면이 같은 고객사를 두고 다른 말을 했다.
//   폴백의 갈림길은 basis 칸의 **유무**다(값이 아니라 유무):
//     • 서버가 판정했고 기준 계약이 없다(basis = 'sale-only' / 'live-sale-only' /
//       'no-contract') → 칸이 비는 것이 **사실**이다. 왜 비었는지는 아래 문구가 말한다.
//     • 서버가 판정 자체를 내리지 않는다(구버전 — basis 칸이 아예 없다) →
//       종전 화면대로 최신 계약. 구분하지 않으면 한쪽에서 반납일이 통째로 사라지거나
//       (조용한 유실) 다른 쪽에서 판매 계약의 반납일이 되살아난다(사양 위반).
//
// ✅ **문구 정본 통합 완료**(다계약 표시 차수3 정정2, 26-08-06) — 앞 차수에는 시트
//   (pages/business/CustomerMasterPage)가 이 규칙의 사본(자체 resolveTermBasis · 같은 문구
//   리터럴)을 들고 있었고, 두 담당이 같은 시간에 같은 파일을 고칠 수 없어 시험이 시트 원본을
//   글자로 대조하고 있었다. 이제 시트가 이 자리를 import 한다 — 판정도 문구도 한 벌이다.
//   사본이 다시 생기면 term-basis.test.ts 의 '사본 금지' 묶음이 깨진다(부탁이 아니라 그물).

/** 네 칸(계약기간·도입일·반납일·D-Day)과 헤더 D-Day 가 읽는 값. */
export interface TermBasisFacts {
  id: string;
  humanId: string;
  rentalTermMonths: number | null;
  installCompletedAt: string | null;
  endDate: string | null;
  daysRemaining: number | null;
}

/**
 * 무엇을 보고 있는가 / 왜 비었는가.
 *   • 'subscription'    — 먼저 끝나는 **판매 전용이 아닌** 계약을 따른다(정상 경로).
 *   • 'sale-only'       — 반납을 지켜볼 계약이 **살아 있지 않다**. 서버가 두 모양을 이 한 값으로
 *                         합쳤다(정정5, 26-08-06): ⓐ 계약이 전부 판매 전용이거나, ⓑ 판매가 아닌
 *                         계약이 있지만 전부 해지됐고 **진행 중인 판매도 없다**. 그래서 화면은
 *                         "이 고객사의 계약은 판매뿐"이라고 말할 수 없다 — ⓑ 에서 거짓이다.
 *   • 'live-sale-only'  — 정정3(26-08-06) 새 값. **진행 중(ACTIVE)인 계약은 전부 판매**이고,
 *                         판매가 아닌 계약은 있지만 전부 해지됐다. 빈 칸이라는 답은 'sale-only'
 *                         와 같지만 **이유가 다르다** — 이 고객사에는 판매가 아닌 계약이 (해지된
 *                         채로) 있으므로 "계약은 판매뿐"이라고 말하면 거짓이다.
 *   • 'no-contract'     — 계약이 하나도 없다.
 *   • 'legacy-latest'   — 서버가 이 판정을 내리지 않는다(구버전) → 종전대로 최신 계약.
 * 뒤 네 값은 **같은 빈 칸(또는 같은 값)의 서로 다른 이유**라 화면이 같은 말을 하지 않는다.
 *
 * ⚠ **해지된 그 계약이 '구독'인지는 아무도 모른다.** 서버의 후보 규칙은 '구독'이 아니라
 *   '판매 전용이 아닌 것'이고, 줄이 0개인 계약(운영 경로가 실제로 만든다 — 계약 조건만 먼저
 *   입력한 계약)은 정본상 판매 전용이 아니라서 그 후보에 들어간다. 그 계약의 계약 형태는
 *   **미상**이다(서버 요약의 contractType 이 null 이고 같은 행의 배지가 '–' 로 적는다).
 *   그래서 아래 문구는 어디에서도 '구독'이라고 부르지 않는다 — 서버 정본이 같은 이유로
 *   금지한 문장이다(apps/api/src/customer-master/customer-master.row.ts pickTermBasisContract).
 */
export type TermBasisReason =
  | 'subscription'
  | 'sale-only'
  | 'live-sale-only'
  | 'no-contract'
  | 'legacy-latest';

export interface TermBasis {
  facts: TermBasisFacts | null;
  reason: TermBasisReason;
}

/** 이 행의 기준 계약과 그 이유. 화면은 여기서 나온 결과만 읽고 스스로 계약을 고르지 않는다. */
export function resolveTermBasis(
  row: Pick<CustomerMasterRow, 'contract' | 'soonestContract' | 'soonestContractBasis'>,
): TermBasis {
  // 서버가 판정을 내렸는가 — basis 칸의 유무가 그 신호다(값이 아니라 유무를 본다).
  if (row.soonestContractBasis === undefined) {
    const legacy = row.soonestContract ?? row.contract ?? null;
    return { facts: legacy, reason: 'legacy-latest' };
  }
  if (row.soonestContract) return { facts: row.soonestContract, reason: 'subscription' };
  // 정정3(26-08-06) — 판매가 이유인 값들은 **그대로 옮긴다**. 종전엔 'sale-only' 하나만 알아보고
  // 나머지를 전부 'no-contract' 로 접었다. 서버가 'live-sale-only' 를 내리기 시작하면 그 접힘이
  // "기준 계약을 받지 못해 비웠습니다"(= 서버 응답이 부실하다는 뜻)로 나가 **이유를 잘못 말한다** —
  // 실제로는 서버가 정확히 판정했고 그 고객사에 지켜볼 반납일이 없는 것이다.
  const basis = row.soonestContractBasis;
  return {
    facts: null,
    reason: basis === 'sale-only' || basis === 'live-sale-only' ? basis : 'no-contract',
  };
}

/**
 * 정정3(26-08-06) — **지켜볼 반납일이 없다**는 같은 답의 두 이유. 답(네 칸이 빈다)이 같아서
 * 화면의 갈림길은 하나면 되지만, **글자는 갈려야 한다** — 한쪽 문장이 다른 쪽 고객사에서
 * 거짓이 되기 때문이다. 그래서 이 좁은 타입 하나로 두 곳(갈림길·글자)을 함께 묶는다.
 */
export type TermBasisNoReturnReason = Extract<TermBasisReason, 'sale-only' | 'live-sale-only'>;

/**
 * 이 행이 **판매 때문에** 네 칸을 비웠는가 — 비웠으면 그 이유를, 아니면 null.
 *
 * 화면이 `reason === 'sale-only'` 를 직접 비교하지 않고 이 함수를 부른다. 값이 하나 늘 때마다
 * 비교문을 든 자리를 전부 찾아 고쳐야 하면 한 자리가 빠지고(이번 정정3이 그 빠진 자리다),
 * 빠진 자리는 **조용히** 배지·안내가 사라지는 모양으로 나타난다(빈 칸만 남는다).
 * 반환값이 좁은 타입이라 아래 두 글자 함수가 갈림길을 다시 묻지 않는다.
 */
export function termBasisNoReturnReason(reason: TermBasisReason): TermBasisNoReturnReason | null {
  return reason === 'sale-only' || reason === 'live-sale-only' ? reason : null;
}

/**
 * 판매 전용 고객사의 빈 칸에 붙는 **짧은 글자**(시트 배지와 같은 글자).
 * 판매 전용은 **정상 상태**다 — 경고 색을 쓰지 않는다.
 */
export const TERM_BASIS_SALE_ONLY_LABEL = '판매, 반납 없음';

/**
 * 정정3(26-08-06) — '판매만 진행 중인' 고객사의 배지 글자.
 * '판매 — 반납 없음' 을 **재사용하지 않는다**: 그 글자는 "계약이 판매뿐"이라는 뜻으로 읽히고
 * 이 고객사에는 판매가 아닌 계약이 (해지된 채로) 있다. 여기서도 경고 색을 쓰지 않는다 —
 * 회수할 것이 없다는 사실은 이상 상태가 아니다.
 *
 * 정정5(26-08-06) — '살아 있음'이 이제 **사실**이다. 서버가 이 값을 진행 중(ACTIVE)인 판매가
 * 있을 때만 내리게 좁혔다(종전엔 EXPIRED 판매 한 건으로도 열려, 같은 행의 진행 상태가 '종료'
 * 인데 배지는 '살아 있음'이라 말했다).
 */
export const TERM_BASIS_LIVE_SALE_ONLY_LABEL = '판매만 살아 있음, 반납 없음';

/** 배지 글자 — 두 이유가 **다른 단어**를 쓴다(같은 단어면 운영자가 두 상황을 구분할 수 없다). */
export function termBasisNoReturnLabel(reason: TermBasisNoReturnReason): string {
  return reason === 'sale-only' ? TERM_BASIS_SALE_ONLY_LABEL : TERM_BASIS_LIVE_SALE_ONLY_LABEL;
}

/**
 * 정정5(26-08-06) — 네 칸이 **어떤 계약을 따르는가**. 서버의 후보 규칙 그대로의 말이다.
 *
 * 🔴 종전 화면은 이 규칙을 "구독 계약만 따릅니다" 라고 적었다. 서버의 규칙은 '구독'이 아니라
 *   **'판매 전용이 아닌 것'** 이고(customer-master.row.ts: `contracts.filter((c) =>
 *   !isSaleOnlyContract(c.lines))`), 그 후보에는 **줄이 0개인 계약**(형태 미상)과 판매·구독이
 *   섞인 계약도 들어간다. 그래서 그 문장은 두 자리에서 거짓이 됐다:
 *     · 줄 0개 계약이 기준으로 뽑힌 행 — 툴팁은 '구독 계약'이라 부르는데 같은 행의 '계약 N건'
 *       배지는 그 계약의 형태를 '–'(미상)로 적는다. 한 행의 두 안내가 갈렸다.
 *     · 계약이 전부 해지된 고객사 — 서버가 해지 제외를 푼 폴백으로 뽑은 계약인데 화면은
 *       '먼저 끝나는 구독 계약'이라 불렀다(그 갈래를 구분할 값이 응답에 없다).
 *   후보 **규칙**을 적으면 두 경우 모두 참이다 — 화면은 자기가 아는 것만 말한다.
 */
export const TERM_BASIS_CANDIDATE_NOUN = '판매 전용이 아닌 계약';
export const TERM_BASIS_CANDIDATE_RULE_TEXT = `${TERM_BASIS_CANDIDATE_NOUN}만 따릅니다. 판매는 소유가 고객사로 넘어가 반납일이 없습니다`;

/** 툴팁 머리말이 기준 계약을 부르는 말(괄호 안). 그 계약을 '구독'이라 단정하지 않는다. */
export const TERM_BASIS_PICK_RULE_TEXT =
  '먼저 끝나는 계약을 고르고, 판매 전용 계약은 후보에서 뺍니다';

/**
 * 정정5(26-08-06) — **진행 상태** 칸이 그 기준 계약의 무엇을 말하는가. 두 화면(시트 배지 안내 ·
 * 고객사 상세 헤더 라벨)이 같은 글자를 쓴다.
 *
 * 🔴 왜 정본으로 올렸나 — 두 화면이 각각 '배정·설치 단계' 라는 리터럴을 들고 있었는데, 이번
 *   차수에 서버가 그 칸에 **'종료'** 를 새로 넣었다(판매 전용 계약이 만기·해지된 고객사 —
 *   customer-master.row.ts synthesizeStatus). '종료'는 배정·설치의 한 단계가 아니라 '계약이
 *   끝났고 회수할 것이 없다'는 값이라, 두 화면의 설명이 함께 뒤처졌다. 같은 규칙을 설명하는
 *   글자가 두 벌이면 다음에도 한쪽만 고쳐진다.
 */
export const STATUS_BASIS_STAGE_TEXT = '배정, 설치, 종료로 나뉘는 진행 단계';

/**
 * 그 빈 칸이 **왜** 비었는지 — 사람이 읽는 한 문장(시트 배지 안내 · 상세 헤더 배지 제목).
 *
 * @param blankColumns 비운 칸의 이름(시트는 네 칸 '계약기간 · 도입일 · 반납일 · D-Day',
 *                     고객사 상세 헤더는 'D-Day' 하나). 화면마다 비는 칸이 다르므로 칸 이름만
 *                     받고 **문장은 한 벌**로 둔다.
 *
 * 정정3(26-08-06) — 이유별로 문장이 갈린다. 종전 한 문장('이 고객사의 계약은 판매뿐입니다')을
 * 두 경우에 함께 쓰면 'live-sale-only' 고객사에서 **거짓**이 된다 — 그 고객사에는 판매가 아닌
 * 계약이 있다(끝났을 뿐이다). 끝난 그 계약의 반납일을 화면에서 지워 버리는 것도 아니라는 사실을
 * 함께 적는다: 회수가 남아 있을 수 있고, 그 날짜는 계약별 안내에 그대로 있다(그 사실은 시험이
 * 못박는다 — CustomerMasterPage.test '해지된 계약의 반납일은 계약별 안내에 남는다').
 *
 * 🔴 정정5(26-08-06) — 두 문장에서 **'구독'이라는 단어를 뺐다.** 두 갈래 모두 그 단어가
 *   지어낸 사실이었다:
 *     · 'live-sale-only' — "구독 계약은 해지되었고" 라고 적었는데, 해지된 그 계약은 계약 형태가
 *       **미상**일 수 있다(줄 0개 계약. 서버 정본이 같은 자리에 "화면도 '구독 계약이
 *       해지되었습니다'라고 적어서는 안 된다"고 못박아 두었다). 같은 행의 배지는 그 계약을
 *       '–'(미상)라고 적고 있어, 한 행의 두 안내가 같은 계약을 두고 갈렸다.
 *     · 'sale-only'      — "이 고객사의 계약은 판매뿐입니다" 가 서버 정정5 뒤로 **좁아졌다**.
 *       그 값에는 '판매가 아닌 계약이 (해지된 채로) 있고 진행 중인 판매도 없는' 고객사가
 *       함께 들어온다(서버가 이유를 알면서 값을 합쳤고 고칠 자리를 이 파일로 넘겼다).
 *   그래서 두 문장 모두 **서버가 실제로 보장하는 것만** 말한다: 판매가 아닌 계약이 (있더라도)
 *   살아 있지 않다는 것. 계약 형태는 계약별 안내가 사실대로 말한다.
 */
export function termBasisNoReturnExplanation(
  reason: TermBasisNoReturnReason,
  blankColumns: string,
): string {
  if (reason === 'sale-only') {
    // ⚠ '반납일을 지켜볼 **계약이 없습니다**' 로 쓰지 않는다 — 그 조각만 읽히면 "이 고객사에
    //   계약이 없다"로 읽히고, 그것이 이 배지가 애초에 막으려던 오해다(계약은 있고 반납이라는
    //   사건만 없다). 툴팁 머리말과 같은 어순('지켜볼 반납일이 없습니다')을 쓴다.
    return (
      '지켜볼 반납일이 없습니다. 판매는 소유가 고객사로 넘어가 반납이라는 사건이 없고, ' +
      '판매가 아닌 계약이 있었더라도 모두 끝났습니다. ' +
      `그래서 ${blankColumns} 를 비워 두었습니다. 누락이 아닙니다.`
    );
  }
  return (
    '이 고객사에서 진행 중인 계약은 판매뿐입니다. 판매는 소유가 고객사로 넘어가 반납이라는 ' +
    '사건이 없습니다. 판매가 아닌 계약도 있지만 모두 해지되었습니다. ' +
    `그래서 ${blankColumns} 를 비워 두었습니다. 누락이 아닙니다. 해지된 그 계약의 반납일은 ` +
    '계약별 안내에 그대로 있습니다.'
  );
}

/**
 * 정정3(26-08-06) — 네 칸 툴팁이 **기준 계약이 없을 때** 머리말로 적는 한 문장.
 *
 * 위 termBasisNoReturnExplanation 과 **자리가 다르다**(툴팁 머리말 vs 배지 안내·헤더 배지 제목).
 * 이 자리는 답을 먼저 말한다 — 툴팁을 여는 사람은 이미 빈 칸을 보고 열었으므로 첫 마디가
 * '지켜볼 반납일이 없습니다' 여야 한다. 두 문장이 **각각 한 벌**인 것이 요점이고 한 문장으로
 * 합치는 것이 요점이 아니다.
 *
 * 🔴 이 문장이 왜 lib 으로 왔나 — 종전에는 CustomerMasterPage 안의 문자열 리터럴이었고,
 *   'sale-only' 가 아닌 모든 값이 '기준 계약을 받지 못해…' 로 접혀 있었다. 그 접힘은
 *   'live-sale-only' 가 오는 순간 **이유를 잘못 말한다**(서버는 정확히 판정했다). 사본이
 *   페이지 안에 있으면 이 파일을 고쳐도 그 자리가 남는다 — 정본 밖의 글자는 그물에 걸리지 않았다.
 *
 * @param blankColumns 비운 칸을 부르는 말. 문장이 `…{blankColumns}을 비웠습니다` 로 끝나므로
 *                     **받침이 있는 말**을 넘긴다(시트는 '이 네 칸').
 */
export function termBasisBlankCellsTip(reason: TermBasisReason, blankColumns: string): string {
  const noReturn = termBasisNoReturnReason(reason);
  if (noReturn === 'sale-only') {
    return (
      '지켜볼 반납일이 없습니다. 판매는 소유가 고객사로 넘어가 반납이라는 사건이 없고, ' +
      `판매가 아닌 계약이 있었더라도 모두 끝났습니다. 그래서 ${blankColumns}을 비웠습니다.`
    );
  }
  if (noReturn === 'live-sale-only') {
    return (
      '지켜볼 반납일이 없습니다. 진행 중인 계약은 판매뿐이고 판매가 아닌 계약은 모두 ' +
      '해지되었습니다. 판매는 소유가 고객사로 넘어가 반납이라는 사건이 없습니다. 그래서 ' +
      `${blankColumns}을 비웠습니다. 해지된 그 계약의 반납일은 아래 계약별 줄에 그대로 있습니다.`
    );
  }
  return `기준 계약을 받지 못해 ${blankColumns}을 비웠습니다.`;
}

// ============================================================
// 다계약 표시 차수3 정정2(26-08-06) — **계약 한 건**의 반납일 표기
// ============================================================
//
// 위 문구들은 '고객사'를 두고 하는 말이다(네 칸이 왜 비었나). 여기부터는 **계약 한 건**을
// 두고 하는 말이다 — 판매 전용 계약은 그 계약 자신의 반납일·D-Day 를 말하지 않는다.
//
// 🔴 무엇이 틀렸었나 — 이 규칙이 26-08-06 에 시트 안에서만 세워졌다. 고객사 상세의 계약
//   카드는 그대로였고, 그래서 같은 계약 하나를 두고 두 화면이 다른 말을 했다:
//     · 시트 '계약 N건' 배지 → 'CNT-2026-0114 … 반납일 없음 (판매 — 소유가 …)'
//     · 상세 계약 카드      → '반납일 2029.08.03 D-1094'
//   운영 위험은 그 D-1094 다. 운영자가 회수 임박으로 읽고 회수 일정을 잡는데, 그 로봇은
//   이미 고객사 소유라 회수할 것이 **없다**.
//
// **계약기간·도입일은 그대로 적는다** — 그 두 값은 그 계약에 실제로 일어난 사실이고(판매도
// 설치는 한다), 없는 사건을 말하는 것은 반납일·D-Day 쪽뿐이다. 빈칸으로 두지 않는 이유도
// 같다: 빈칸은 '미입력'으로 읽히므로 날짜 대신 **없는 이유**를 적는다.
//
// 판매 전용 여부를 함수가 스스로 판정하지 않고 **받는다**. 줄 원문이 없는 부르는 자리가
// 있기 때문이다 — 시트의 네 칸 툴팁은 기준 계약 요약(TermBasisFacts)만 들고 있어 그 계약의
// 줄을 볼 수 없고, 그 자리는 시트 칸이 지금 보여 주는 값과 같은 글자를 적어야 한다
// (감추면 툴팁이 칸과 다른 말을 한다 — CustomerMasterPage TermBasisTip 주석).

/** 반납일·D-Day 조각이 읽는 값. 계약 요약·계약 카드·최신 계약 어디에나 있는 두 칸이다. */
export interface EndDateFacts {
  endDate: string | null;
  daysRemaining: number | null;
}

/**
 * 반납일 자리에 무엇을 적는가.
 *   • saleOnly: true  — 날짜가 아니라 **없는 이유**(text) 한 마디.
 *   • saleOnly: false — 날짜(date, 없으면 '–')와 D-Day(dday, 없으면 null).
 * 조각만 돌려주고 **배치는 부르는 화면이 정한다** — 시트는 한 줄 문자열, 상세 카드는 D-Day 를
 * 흐린 색 span 으로 붙인다. 글자를 한 벌로 두는 것이 목적이고 레이아웃을 한 벌로 두는 것이
 * 목적이 아니다(레이아웃을 억지로 합치면 두 화면 중 하나의 DOM 이 이유 없이 바뀐다).
 */
export type EndDateDisplay =
  | { saleOnly: true; text: string }
  | { saleOnly: false; date: string; dday: string | null };

/** 판매 전용 계약의 반납일 자리에 오는 **글자 한 벌**(날짜가 아니라 없는 이유). */
export const SALE_ONLY_END_DATE_TEXT = '없음, 판매라서 소유가 고객사로 넘어갔습니다';

/**
 * 이 계약이 **판매 전용**인가 — 서버가 내린 줄 원문(contracts[].robots[].reason)을 shared
 * 정본에 그대로 넘긴다. 화면이 새로 판정하지 않는다.
 *
 * ⚠ 줄이 **0개**(또는 응답에 줄이 없는 옛 서버)인 계약은 판매 전용이 아니다 — 정본의 규칙이다
 *   ('판매 전용'은 줄에 대한 주장이라 줄이 없으면 할 수 없다). 그래서 줄을 내리지 않는
 *   응답에서는 종전대로 반납일이 보인다. 모르는 것을 판매로 지어내지 않는다.
 */
export function isSaleOnlyByLines(
  lines: ReadonlyArray<{ reason: string }> | null | undefined,
): boolean {
  return isSaleOnlyContract(lines ?? []);
}

/** 반납일 자리의 조각. 날짜·D-Day 표기는 리포 정본(kstYmd · formatDdayOrNull)을 쓴다. */
export function endDateDisplay(f: EndDateFacts, saleOnly: boolean): EndDateDisplay {
  if (saleOnly) return { saleOnly: true, text: SALE_ONLY_END_DATE_TEXT };
  return {
    saleOnly: false,
    date: kstYmd(f.endDate) ?? '–',
    dday: formatDdayOrNull(f.daysRemaining),
  };
}

/**
 * '반납일 …' **한 줄 문자열** — 여러 값을 가운뎃점으로 잇는 자리(시트의 네 칸 툴팁·배지 줄).
 *
 * @param endDateLabel 칸 이름. 화면이 자기 열 이름 정본에서 넘긴다(시트 COLUMN_LABEL.endDate) —
 *                     여기에 리터럴로 적으면 열 이름을 고친 날 안내문만 옛 이름을 말한다.
 */
export function endDateFieldText(endDateLabel: string, f: EndDateFacts, saleOnly: boolean): string {
  const d = endDateDisplay(f, saleOnly);
  if (d.saleOnly) return `${endDateLabel} ${d.text}`;
  return `${endDateLabel} ${d.date}${d.dday === null ? '' : ` (${d.dday})`}`;
}

// ============================================================
// 다계약 표시 차수3 정정2(26-08-06) — **진행 상태**가 보는 계약
// ============================================================
//
// 네 칸(위 resolveTermBasis)과 **독립**이다. 판매만 있는 고객사에서 갈린다 — 네 칸은 '–' 인데
// 상태는 그 판매 계약의 진행 단계다. 구버전 서버(정정3 이전)에서는 '판매만 진행 중인' 고객사도
// 갈렸다: 상태는 살아 있는 판매 계약, 네 칸은 해지된 그 판매 아닌 계약. 지금 서버는 그 경우
// 네 칸을 비우지만(basis = 'live-sale-only') **콘솔이 먼저 배포되는 구간**에는 옛 응답이 온다.
// 두 값이 다른 계약을 볼 수 있다는 사실이 응답에 있으므로 화면은 짐작하지 않는다.

/** 이 계약이 **진행 상태 칸의 기준**인가. 이 칸을 내리지 않는 구버전은 임박 계약으로 폴백. */
export function isStatusBasisContract(c: { isSoonest: boolean; isStatusBasis?: boolean }): boolean {
  return c.isStatusBasis ?? c.isSoonest;
}

/** 진행 상태 배지가 보는 계약(없으면 null — 지어내지 않는다). */
export function pickStatusBasisContract<T extends { isSoonest: boolean; isStatusBasis?: boolean }>(
  contracts: readonly T[] | null | undefined,
): T | null {
  return (contracts ?? []).find(isStatusBasisContract) ?? null;
}
